import { useEffect, useState, useCallback } from "react";
import { warroomApi } from "@/lib/api-client";
import { Swords, ChevronDown, ChevronUp, TrendingUp, TrendingDown, CheckCircle2 } from "lucide-react";
import { AGENT_META, relativeTime, fmtUSDFull } from "@/lib/format";

export default function BattlePlanHistory({ refreshKey }) {
  const [plans, setPlans] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const p = await warroomApi.listPlans();
      setPlans(p);
    } catch (e) { console.error("battle plan load failed", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [refreshKey, load]);

  return (
    <section className="uc-card p-6 uc-fade" data-testid="battle-plan-history">
      <div className="mb-4">
        <div className="uc-chip mb-2"><Swords size={11} className="text-red-400"/> BATTLE PLAN HISTORY</div>
        <h2 className="font-serif-display text-2xl">Campaign Timeline</h2>
        <p className="text-white/50 text-xs mt-1">Every War Room deployment with progress on projected outcomes.</p>
      </div>

      {plans.length === 0 ? (
        <div className="p-6 border border-white/5 bg-black/40 text-white/40 text-xs italic text-center">
          No battle plans yet. Engage the War Room to draft your first campaign.
        </div>
      ) : (
        <div className="space-y-3 max-h-[500px] overflow-y-auto uc-scroll -mr-2 pr-2">
          {plans.map((p) => {
            const isOpen = expanded === p.id;
            const pct = p.progress?.pct || 0;
            return (
              <div key={p.id} className="border border-white/10 hover:border-white/20 transition-colors" data-testid={`plan-${p.id}`}>
                <button
                  data-testid={`plan-toggle-${p.id}`}
                  onClick={() => setExpanded(isOpen ? null : p.id)}
                  className="w-full p-4 flex items-center gap-3 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm text-white font-medium">{p.title}</span>
                      <span className="font-mono-uc text-[10px] text-white/40 uppercase tracking-wider">
                        · {relativeTime(p.created_at)}
                      </span>
                    </div>
                    <div className="text-[11px] text-white/50 truncate">{p.objective}</div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex-1 h-1 bg-white/10 relative max-w-md">
                        <div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }}/>
                      </div>
                      <div className="font-mono-uc text-[10px] text-white/60">
                        {p.progress?.completed || 0}/{p.progress?.total || 0} · {pct}%
                      </div>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp size={14} className="text-white/40"/> : <ChevronDown size={14} className="text-white/40"/>}
                </button>

                {isOpen && (
                  <div className="border-t border-white/5 bg-black/40 p-4 space-y-3" data-testid={`plan-detail-${p.id}`}>
                    {p.steps.map((s, i) => {
                      const meta = AGENT_META[s.agent] || AGENT_META.scout;
                      const task = (p.tasks || []).find((t) => (t.title || "").includes(`Step ${s.n}`));
                      const completed = task?.status === "completed";
                      return (
                    <div key={`step-${s.n}`} className="border border-white/10" style={{ borderLeft: `2px solid ${meta.color}` }}>
                          <div className="p-3 flex items-center gap-3 border-b border-white/5">
                            <div className="font-serif-display text-xl text-white/30 w-6">{s.n}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-mono-uc text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
                                <span className="text-white/40 text-xs">· ETA {s.eta_days}d</span>
                                {s.cost_estimate > 0 && <span className="text-white/50 font-mono-uc text-[10px]">· {fmtUSDFull(s.cost_estimate)}</span>}
                                {completed && (
                                  <span className="font-mono-uc text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1">
                                    <CheckCircle2 size={10}/> Complete
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-white/85">{s.action}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/5">
                            <div className="p-2.5">
                              <div className="font-mono-uc text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1 mb-0.5">
                                <TrendingUp size={9}/> Best
                              </div>
                              <div className="text-white/70 text-[11px]">{s.best_case}</div>
                            </div>
                            <div className="p-2.5">
                              <div className="font-mono-uc text-[10px] uppercase tracking-widest text-red-400 flex items-center gap-1 mb-0.5">
                                <TrendingDown size={9}/> Worst
                              </div>
                              <div className="text-white/70 text-[11px]">{s.worst_case}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
